import type { components, paths } from "@/api-schema";
import { PathsV1VehicleValuationUuidGetParametersQueryExtend, VehicleValuationStatus } from "@/api-schema-enums";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const VEHICLE_VALUATION_RESOURCE = "vehicle-valuation" as const;
export const VEHICLE_VALUATION_SEARCH_OPERATION = "search" as const;
export const VEHICLE_VALUATION_SEARCH_API_PATH =
  `/v1/${VEHICLE_VALUATION_RESOURCE}/${VEHICLE_VALUATION_SEARCH_OPERATION}` as const;

type GeneratedVehicleValuationSearchBody =
  paths[typeof VEHICLE_VALUATION_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
type GeneratedVehicleValuationSearchResponse =
  paths[typeof VEHICLE_VALUATION_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

type VehicleValuation = components["schemas"]["VehicleValuation"];

export const VEHICLE_VALUATION_EXTEND_FIELDS = [
  PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle,
  PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_ModelGeneration,
  PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily,
  PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily_Brand,
  PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_OwningBranch,
  PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_CurrentBranch,
] as const;
export type VehicleValuationExtend = (typeof VEHICLE_VALUATION_EXTEND_FIELDS)[number];

export type VehicleValuationSearchBody = Omit<GeneratedVehicleValuationSearchBody, "status"> & {
  status?: VehicleValuationStatus[];
};

export type VehicleValuationSearchParameters =
  paths[typeof VEHICLE_VALUATION_SEARCH_API_PATH]["post"]["parameters"]["query"];

export type VehicleValuationSearchResponse = Omit<GeneratedVehicleValuationSearchResponse, "data"> & {
  data: VehicleValuation[];
};

export function useVehicleValuationSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<VehicleValuationSearchParameters, VehicleValuationSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    VehicleValuationSearchResponse,
    Error,
    VehicleValuationSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<VehicleValuationSearchParameters, VehicleValuationSearchBody>>(
      options,
    );
  const params = handlePaginatedParameters(optionsWithDefaults.parameters);
  const body = optionsWithDefaults.body;

  return useQuery({
    queryKey: [VEHICLE_VALUATION_RESOURCE, VEHICLE_VALUATION_SEARCH_OPERATION, { params, body }],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<VehicleValuationSearchResponse>(
        VEHICLE_VALUATION_SEARCH_API_PATH,
        body,
        {
          params,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
